import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { Business, BusinessForm } from '../types/business';
import { handleError, validateBusinessForm } from '../utils/businessValidation';
import { useToast } from '../components/ui/Toast';

export const useBusinessData = () => {
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<BusinessForm>({
    name: "",
    description: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    city: "",
    province: ""
  });

  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  const fetchBusinessData = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    try {
      console.log('Fetching business data...');
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("businesses")
        .select("*")
        .eq("owner_id", user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      console.log('Business data fetched:', data);
      console.log('Logo URL in business data:', data?.logo_url);
      setBusiness(data || null);
    } catch (err: any) {
      console.error('Error fetching business data:', err);
      setError(err.message || "Failed to load business data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      
      // Validate form data
      const validationErrors = validateBusinessForm(form);
      if (validationErrors.length > 0) {
        showErrorToast(`Validation errors: ${validationErrors.join(', ')}`);
        return;
      }

      if (!business?.id) {
        throw new Error("Business ID not found");
      }

      const { error } = await (supabase as any)
        .from("businesses")
        .update({
          name: form.name.trim(),
          description: form.description.trim(),
          contact_email: form.contact_email.trim().toLowerCase(),
          contact_phone: form.contact_phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          province: form.province.trim()
        })
        .eq("id", business.id);
        
      if (error) {
        throw new Error(handleError(error, "Failed to update business profile"));
      }
      
      showSuccessToast("Profile updated successfully");
      setEditMode(false);
      await fetchBusinessData();
    } catch (err: any) {
      showErrorToast(handleError(err, "Failed to update profile"));
    } finally {
      setLoading(false);
    }
  };

  // Initialize form when business data loads
  useEffect(() => {
    if (business) {
      setForm({
        name: business.name || "",
        description: business.description || "",
        contact_email: business.contact_email || "",
        contact_phone: business.contact_phone || "",
        address: business.address || "",
        city: business.city || "",
        province: business.province || ""
      });
    }
  }, [business]);

  useEffect(() => {
    fetchBusinessData();
  }, [user]);

  return {
    business,
    loading,
    error,
    editMode,
    form,
    setEditMode,
    handleInputChange,
    handleProfileUpdate,
    fetchBusinessData
  };
};